import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Ban,
  Bug,
  CheckCircle2,
  Copy,
  KeyRound,
  Minimize2,
  RefreshCw,
  RotateCcw,
  Route,
  ShieldCheck,
  UserCog,
  X,
} from 'lucide-react';

import { Badge } from '@repo/ui/primitives/badge';
import { Button } from '@repo/ui/primitives/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/primitives/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/primitives/tabs';

import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';

import {
  ACCESS_LAB_AVAILABLE,
  ACCESS_LAB_PREFERENCE_EVENT,
  readAccessLabEnabled,
} from './access-lab-preferences.js';
import {
  SESSION_ROLE_PREVIEW,
  buildAccessLabSnapshot,
  buildCopyContext,
  getMembershipRoles,
  getMemberships,
  getRoleId,
  getRoleName,
  getTenantIdFromMembership,
  getTenantNameFromMembership,
} from './access-lab-utils.js';

const ACCESS_LAB_STORAGE_KEY = 'access-lab-open';

function readStoredOpen() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(ACCESS_LAB_STORAGE_KEY) === 'true';
}

function writeStoredOpen(open) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ACCESS_LAB_STORAGE_KEY, open ? 'true' : 'false');
}

function roleSummary(roles) {
  if (!roles.length) return 'Rol yok';
  if (roles.length === 1) return getRoleName(roles[0]);
  return `${getRoleName(roles[0])} +${roles.length - 1}`;
}


function AccessActionRow({ row }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_88px_minmax(0,1.2fr)] items-center gap-[var(--ds-space-control-gap)] rounded-[var(--ds-radius-control)] border border-border bg-background/70 px-[var(--ds-space-3)] py-[var(--ds-space-2)] text-xs">
      <span className="truncate font-medium text-foreground">{row.label}</span>
      <Badge variant={row.allowed ? 'default' : 'secondary'} className="justify-center rounded-[var(--ds-radius-pill)] text-[10px]">
        {row.allowed ? 'Allowed' : 'Denied'}
      </Badge>
      <span className="truncate text-muted-foreground">{row.reason}</span>
    </div>
  );
}

function AccessSummary({ snapshot }) {
  const allowedCount = snapshot.actions.filter((row) => row.allowed).length;
  const totalCount = snapshot.actions.length;

  if (!snapshot.pageKey) {
    return (
      <div className="rounded-[var(--ds-radius-control)] border border-warning/40 bg-warning/10 px-[var(--ds-space-3)] py-[var(--ds-space-2)] text-xs text-[var(--warning-tint-foreground)]">
        Bu route için permission registry eşleşmesi bulunamadı.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 text-xs">
      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-2)]">
        <div className="text-muted-foreground">PageKey</div>
        <div className="truncate font-semibold text-foreground">{snapshot.pageKey}</div>
      </div>
      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-2)]">
        <div className="text-muted-foreground">Module</div>
        <div className="truncate font-semibold text-foreground">{snapshot.pageModule || '-'}</div>
      </div>
      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-2)]">
        <div className="text-muted-foreground">Aksiyon</div>
        <div className="font-semibold text-foreground">{allowedCount}/{totalCount}</div>
      </div>
    </div>
  );
}

function PersonaTab({ snapshot, memberships, selectedTenantId, onTenantChange, previewRoleId, onPreviewRoleChange, onReset }) {
  const roles = getMembershipRoles(snapshot.membership);

  return (
    <div className="space-y-3">
      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-3)]">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-xs text-muted-foreground">Active user</div>
            <div className="text-sm font-semibold text-foreground">{snapshot.userDisplayName}</div>
          </div>
          <Badge variant={snapshot.previewMode ? 'secondary' : 'outline'} className="rounded-[var(--ds-radius-pill)]">
            {snapshot.previewMode ? 'Panel önizleme' : 'Gerçek oturum'}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">{snapshot.user?.email || 'E-posta yok'}</div>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="access-lab-tenant">
          Tenant
        </label>
        <Select value={selectedTenantId || ''} onValueChange={onTenantChange} disabled={memberships.length === 0}>
          <SelectTrigger id="access-lab-tenant" className="rounded-[var(--ds-radius-control)] bg-muted/50">
            <SelectValue placeholder="Select tenant" />
          </SelectTrigger>
          <SelectContent>
            {memberships.map((membership) => {
              const tenantId = getTenantIdFromMembership(membership);
              return (
                <SelectItem key={tenantId} value={tenantId}>
                  {getTenantNameFromMembership(membership)}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="access-lab-role-preview">
          Rol önizleme
        </label>
        <Select value={previewRoleId} onValueChange={onPreviewRoleChange} disabled={roles.length === 0}>
          <SelectTrigger id="access-lab-role-preview" className="rounded-[var(--ds-radius-control)] bg-muted/50">
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SESSION_ROLE_PREVIEW}>Actual session roles</SelectItem>
            {roles.map((role) => {
              const roleId = getRoleId(role);
              return (
                <SelectItem key={roleId} value={roleId}>
                  {getRoleName(role)}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-[11px] leading-snug text-muted-foreground">
          Rol önizleme sadece bu panelin permission hesabını değiştirir; API yetkisini bypass etmez.
        </p>
      </div>

      <div className="rounded-[var(--ds-radius-control)] border border-border bg-muted/40 p-[var(--ds-space-3)] text-xs">
        <div className="mb-2 font-medium text-foreground">Active roles</div>
        <div className="flex flex-wrap gap-1.5">
          {roles.length ? roles.map((role) => (
            <Badge key={getRoleId(role)} variant="outline" className="rounded-[var(--ds-radius-pill)] bg-background">
              {getRoleName(role)}
            </Badge>
          )) : <span className="text-muted-foreground">Rol yok</span>}
        </div>
      </div>

      <Button type="button" variant="outline" size="sm" className="w-full gap-2" onClick={onReset}>
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        Oturum bağlamına dön
      </Button>
    </div>
  );
}

function AccessTab({ snapshot }) {
  return (
    <div className="space-y-3">
      <AccessSummary snapshot={snapshot} />

      {!snapshot.pageLicensed && snapshot.pageKey ? (
        <div className="flex items-start gap-[var(--ds-space-control-gap)] rounded-[var(--ds-radius-control)] border border-warning/40 bg-warning/10 px-[var(--ds-space-3)] py-[var(--ds-space-2)] text-xs text-[var(--warning-tint-foreground)]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Even with role permission, the page is denied if the tenant module license is missing.</span>
        </div>
      ) : null}

      <div className="space-y-2">
        {snapshot.actions.length ? snapshot.actions.map((row) => (
          <AccessActionRow key={row.action} row={row} />
        )) : (
          <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 px-[var(--ds-space-3)] py-[var(--ds-space-6)] text-center text-xs text-muted-foreground">
            Bu route için aksiyon listesi bulunamadı.
          </div>
        )}
      </div>
    </div>
  );
}

function RolePermissionRow({ page }) {
  return (
    <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-3)] text-xs">
      <div className="flex items-start justify-between gap-[var(--ds-space-3)]">
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{page.pageTitle}</div>
          <div className="truncate text-[11px] text-muted-foreground">{page.pageKey}</div>
        </div>
        <Badge variant="secondary" className="shrink-0 rounded-[var(--ds-radius-pill)] text-[10px]">
          {page.allowedCount}/{page.totalCount}
        </Badge>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {page.actions.map((action) => (
          <Badge
            key={`${page.pageKey}-${action.action}`}
            variant={action.allowed ? 'default' : 'outline'}
            className={`rounded-[var(--ds-radius-pill)] text-[10px] ${action.allowed ? '' : 'border-border-subtle text-muted-foreground'}`}
          >
            {action.label}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function RolePermissionsTab({ snapshot }) {
  const visiblePages = snapshot.rolePermissionPages.filter((page) => page.hasAnyPermission);
  const allowedActions = visiblePages.reduce((total, page) => total + page.allowedCount, 0);

  return (
    <div className="space-y-3">
      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-3)] text-xs">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-muted-foreground">Reviewed role set</div>
            <div className="font-semibold text-foreground">{roleSummary(snapshot.roles)}</div>
          </div>
          <Badge variant={snapshot.previewMode ? 'secondary' : 'outline'} className="rounded-[var(--ds-radius-pill)]">
            {snapshot.previewMode ? 'Önizleme' : 'Oturum'}
          </Badge>
        </div>
        <div className="text-muted-foreground">
          {visiblePages.length} sayfada {allowedActions} aksiyon izni var. Bu özet tenant lisansı ve seçili rol önizlemesine göre hesaplanır; API yetkisini değiştirmez.
        </div>
      </div>

      <div className="space-y-2">
        {visiblePages.length ? visiblePages.map((page) => (
          <RolePermissionRow key={page.pageKey} page={page} />
        )) : (
          <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 px-[var(--ds-space-3)] py-[var(--ds-space-6)] text-center text-xs text-muted-foreground">
            Bu tenant/rol için lisanslı sayfalarda verilmiş aksiyon izni bulunamadı.
          </div>
        )}
      </div>
    </div>
  );
}

function DebugTab({ snapshot, copyStatus, onCopy, onRefreshProfile, refreshing }) {
  return (
    <div className="space-y-3">
      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-3)] text-xs">
        <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
          <Route className="h-3.5 w-3.5" aria-hidden="true" />
          Route bağlamı
        </div>
        <dl className="space-y-1.5 text-muted-foreground">
          <div className="flex justify-between gap-3"><dt>Path</dt><dd className="truncate text-right text-foreground">{snapshot.pathname || '-'}</dd></div>
          <div className="flex justify-between gap-3"><dt>Registry</dt><dd className="truncate text-right text-foreground">{snapshot.registryPath || '-'}</dd></div>
          <div className="flex justify-between gap-3"><dt>PageKey</dt><dd className="truncate text-right text-foreground">{snapshot.pageKey || '-'}</dd></div>
          <div className="flex justify-between gap-3"><dt>Licensed</dt><dd className="text-right text-foreground">{snapshot.pageLicensed ? 'Evet' : 'Hayır'}</dd></div>
        </dl>
      </div>

      <div className="rounded-[var(--ds-radius-control)] border border-border bg-background/70 p-[var(--ds-space-3)] text-xs">
        <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Oturum snapshot
        </div>
        <dl className="space-y-1.5 text-muted-foreground">
          <div className="flex justify-between gap-3"><dt>Tenant</dt><dd className="truncate text-right text-foreground">{snapshot.tenantName}</dd></div>
          <div className="flex justify-between gap-3"><dt>Rol</dt><dd className="truncate text-right text-foreground">{roleSummary(snapshot.roles)}</dd></div>
          <div className="flex justify-between gap-3"><dt>Module</dt><dd className="truncate text-right text-foreground">{snapshot.licensedModules.join(', ') || '-'}</dd></div>
        </dl>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onCopy}>
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          {copyStatus || 'Kopyala'}
        </Button>
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onRefreshProfile} disabled={refreshing}>
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
          Yenile
        </Button>
      </div>
    </div>
  );
}

export function AccessLab({ onRefreshProfile }) {
  const { t } = useTranslation('accessLab');
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const setSelectedTenantId = useTenantStore((state) => state.setSelectedTenantId);
  const [enabled, setEnabled] = React.useState(readAccessLabEnabled);
  const [open, setOpen] = React.useState(readStoredOpen);
  const [activeTab, setActiveTab] = React.useState('persona');
  const [previewRoleId, setPreviewRoleId] = React.useState(SESSION_ROLE_PREVIEW);
  const [copyStatus, setCopyStatus] = React.useState('');
  const [refreshing, setRefreshing] = React.useState(false);

  const memberships = React.useMemo(() => getMemberships(user), [user]);
  const snapshot = React.useMemo(
    () => buildAccessLabSnapshot({ user, selectedTenantId, pathname: location.pathname, previewRoleId }),
    [user, selectedTenantId, location.pathname, previewRoleId],
  );

  React.useEffect(() => {
    writeStoredOpen(open);
  }, [open]);

  React.useEffect(() => {
    const handlePreferenceChange = (event) => setEnabled(event.detail.enabled);
    window.addEventListener(ACCESS_LAB_PREFERENCE_EVENT, handlePreferenceChange);
    return () => window.removeEventListener(ACCESS_LAB_PREFERENCE_EVENT, handlePreferenceChange);
  }, []);

  React.useEffect(() => {
    setPreviewRoleId(SESSION_ROLE_PREVIEW);
  }, [selectedTenantId]);

  if (!ACCESS_LAB_AVAILABLE || !enabled || !user) return null;

  const allowedCount = snapshot.actions.filter((row) => row.allowed).length;
  const totalCount = snapshot.actions.length;

  const handleReset = () => {
    setPreviewRoleId(SESSION_ROLE_PREVIEW);
    const defaultMembership = memberships.find((membership) => membership?.isDefault) || memberships[0];
    const defaultTenantId = getTenantIdFromMembership(defaultMembership);
    if (defaultTenantId) setSelectedTenantId(defaultTenantId);
  };

  const handleCopy = async () => {
    const payload = JSON.stringify(buildCopyContext(snapshot), null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      setCopyStatus('Kopyalandı');
    } catch {
      setCopyStatus('Kopyalanamadı');
    }
    window.setTimeout(() => setCopyStatus(''), 1800);
  };

  const handleRefresh = async () => {
    if (typeof onRefreshProfile !== 'function') return;
    setRefreshing(true);
    try {
      await onRefreshProfile();
    } finally {
      setRefreshing(false);
    }
  };

  if (!open) {
    return (
      <div className="fixed bottom-[var(--ds-space-4)] right-[var(--ds-space-4)] z-[var(--ds-layer-transient)] print:hidden">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-auto gap-[var(--ds-space-control-gap)] rounded-[var(--ds-radius-pill)] border-warning/40 px-[var(--ds-space-3)] py-[var(--ds-space-2)]"
          onClick={() => setOpen(true)}
          aria-label={t('open', { defaultValue: 'Open Access Lab panel' })}
        >
          <Bug className="h-4 w-4 text-[var(--warning-tint-foreground)]" aria-hidden="true" />
          <span className="hidden max-w-52 truncate text-xs font-semibold sm:inline">
            Access Lab · {snapshot.previewMode ? 'Preview' : roleSummary(snapshot.roles)}
          </span>
          <Badge variant="secondary" className="rounded-[var(--ds-radius-pill)] text-[10px]">
            {totalCount ? `${allowedCount}/${totalCount}` : 'DEV'}
          </Badge>
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-[var(--ds-space-4)] right-[var(--ds-space-4)] z-[var(--ds-layer-transient)] w-[min(420px,calc(100vw-2rem))] print:hidden">
      <div className="overflow-hidden rounded-[var(--ds-radius-workbench)] border border-border bg-card text-card-foreground shadow-[var(--shadow-command)]">
        <div className="flex items-center justify-between gap-[var(--ds-space-3)] border-b border-border-subtle px-[var(--ds-space-4)] py-[var(--ds-space-3)]">
          <div className="min-w-0">
            <div className="flex items-center gap-[var(--ds-space-control-gap)]">
              <Bug className="h-4 w-4 text-[var(--warning-tint-foreground)]" aria-hidden="true" />
              <span className="text-sm font-semibold">Access Lab</span>
              <Badge variant="warning" className="rounded-[var(--ds-radius-pill)] text-[10px]">
                DEV
              </Badge>
            </div>
            <div className="mt-0.5 truncate text-xs text-muted-foreground">
              {snapshot.tenantName} · {snapshot.pageKey || snapshot.pathname || 'no route'}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="icon" className="size-[var(--ds-density-icon-button-size)]" onClick={() => setOpen(false)} aria-label="Minimize">
              <Minimize2 className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="size-[var(--ds-density-icon-button-size)]" onClick={() => setOpen(false)} aria-label="Close">
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <div className="max-h-[min(720px,calc(100dvh-7rem))] overflow-y-auto p-[var(--ds-space-4)] custom-scrollbar">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid h-[var(--ds-density-control-height)] w-full grid-cols-4">
              <TabsTrigger value="persona" className="gap-1 text-xs">
                <UserCog className="h-3.5 w-3.5" aria-hidden="true" />
                Persona
              </TabsTrigger>
              <TabsTrigger value="role" className="gap-1 text-xs">
                <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                Rol
              </TabsTrigger>
              <TabsTrigger value="access" className="gap-1 text-xs">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Access
              </TabsTrigger>
              <TabsTrigger value="debug" className="gap-1 text-xs">
                <Route className="h-3.5 w-3.5" aria-hidden="true" />
                Debug
              </TabsTrigger>
            </TabsList>

            <TabsContent value="persona" className="mt-3">
              <PersonaTab
                snapshot={snapshot}
                memberships={memberships}
                selectedTenantId={selectedTenantId}
                onTenantChange={setSelectedTenantId}
                previewRoleId={previewRoleId}
                onPreviewRoleChange={setPreviewRoleId}
                onReset={handleReset}
              />
            </TabsContent>

            <TabsContent value="role" className="mt-3">
              <RolePermissionsTab snapshot={snapshot} />
            </TabsContent>

            <TabsContent value="access" className="mt-3">
              <AccessTab snapshot={snapshot} />
            </TabsContent>

            <TabsContent value="debug" className="mt-3">
              <DebugTab
                snapshot={snapshot}
                copyStatus={copyStatus}
                onCopy={handleCopy}
                onRefreshProfile={handleRefresh}
                refreshing={refreshing}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
